import { parseValueByUnit } from '../components/tsrComparisonUtils';
import { getCalendarDueDaysRemaining } from '../components/componentFormUtils';
import { getLiveRawForTsr, liveTsrContextFromRecord } from '../../utils/liveTlbUsageMapping';

/**
 * Typed remaining for Aircraft Lifecycle → Components (days / hours / cycles / landings).
 * Hours are decimal hours. Prefer live Remaining-until-Due when TLB data exists;
 * otherwise fall back to stored remainingTime. Across multiple TSR rows, take the soonest (min).
 */

function emptyRemainingByUnit() {
  return { days: null, hours: null, cycles: null, landings: null };
}

function finiteOrNull(n, roundLandings = false) {
  if (n == null || n === '') return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  if (roundLandings) return Math.round(num);
  return Math.round(num * 100) / 100;
}

function buildRemainingByUnit(partial = {}) {
  return {
    days: finiteOrNull(partial.days),
    hours: finiteOrNull(partial.hours),
    cycles: finiteOrNull(partial.cycles),
    landings: finiteOrNull(partial.landings, true)
  };
}

function takeMin(current, next) {
  if (next == null || !Number.isFinite(Number(next))) return current;
  const n = Number(next);
  if (current == null) return n;
  return Math.min(current, n);
}

function usageDimensionForUnit(unit) {
  const u = String(unit || '').toLowerCase();
  if (u.includes('hour') || u.includes('hobbs')) return 'hours';
  if (u.includes('cycle')) return 'cycles';
  if (u.includes('landing')) return 'landings';
  return null;
}

function hasNonEmpty(v) {
  return v != null && String(v).trim() !== '';
}

/**
 * Live remaining numeric in the same units as parseValueByUnit (minutes for Hours).
 * @returns {number|null}
 */
function computeLiveRemainingNumeric(record, currentValues, effectiveTlbUsageMapping) {
  const unit =
    record.elapsedTime?.unit ||
    record.limit?.unit ||
    record.remainingTime?.unit ||
    record.installedValue?.unit ||
    '';
  if (!unit) return null;

  const { raw: liveRaw } = getLiveRawForTsr(
    currentValues,
    effectiveTlbUsageMapping,
    liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
  );
  const limitRaw = record?.limit?.value;
  const airframeAtInstallRaw = record?.airframeValueAtInstallation?.value;
  const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;

  if (
    liveRaw == null ||
    !hasNonEmpty(limitRaw) ||
    !hasNonEmpty(airframeAtInstallRaw) ||
    !hasNonEmpty(componentElapsedAtInstallRaw)
  ) {
    return null;
  }

  try {
    const airframeLiveNumeric = parseValueByUnit(liveRaw, unit);
    const airframeAtInstallNumeric = parseValueByUnit(airframeAtInstallRaw, unit);
    const componentElapsedAtInstallNumeric = parseValueByUnit(componentElapsedAtInstallRaw, unit);
    const limitNumeric = parseValueByUnit(limitRaw, unit);
    const dueNumeric = airframeAtInstallNumeric + (limitNumeric - componentElapsedAtInstallNumeric);
    return dueNumeric - airframeLiveNumeric;
  } catch (e) {
    return null;
  }
}

/**
 * @param {object} component
 * @param {object|null} currentUsageTotals - from live-usage API
 * @param {object|null} effectiveTlbUsageMapping
 * @returns {{ days: number|null, hours: number|null, cycles: number|null, landings: number|null }}
 */
export function computeComponentRemainingByUnit(
  component,
  currentUsageTotals = null,
  effectiveTlbUsageMapping = null
) {
  const out = emptyRemainingByUnit();
  if (!component || !Array.isArray(component.timeSinceRecords) || component.timeSinceRecords.length === 0) {
    return out;
  }

  const currentValues = currentUsageTotals?.values || null;

  for (const record of component.timeSinceRecords) {
    const unit =
      record.elapsedTime?.unit ||
      record.limit?.unit ||
      record.remainingTime?.unit ||
      record.installedValue?.unit ||
      '';
    const unitTrim = String(unit || '').trim();
    const dim = usageDimensionForUnit(unitTrim);

    const calDays = getCalendarDueDaysRemaining(record);
    if (calDays != null) {
      out.days = takeMin(out.days, calDays);
    } else if (unitTrim === 'Calendar Days' && hasNonEmpty(record.remainingTime?.value)) {
      const days = parseInt(String(record.remainingTime.value).replace(/\D/g, ''), 10);
      if (!Number.isNaN(days)) out.days = takeMin(out.days, days);
    }

    if (!dim) continue;

    let usageRemaining = computeLiveRemainingNumeric(record, currentValues, effectiveTlbUsageMapping);
    if (usageRemaining == null && hasNonEmpty(record.remainingTime?.value) && unitTrim) {
      try {
        usageRemaining = parseValueByUnit(record.remainingTime.value, unitTrim);
      } catch (e) {
        usageRemaining = null;
      }
    }
    if (usageRemaining == null || !Number.isFinite(Number(usageRemaining))) continue;

    if (dim === 'hours') {
      // parseValueByUnit(Hours) is minutes → decimal hours
      out.hours = takeMin(out.hours, Number(usageRemaining) / 60);
    } else if (dim === 'cycles') {
      out.cycles = takeMin(out.cycles, usageRemaining);
    } else if (dim === 'landings') {
      out.landings = takeMin(out.landings, usageRemaining);
    }
  }

  return buildRemainingByUnit(out);
}

export function formatRemainingHoursDisplay(decimalHours) {
  if (decimalHours == null || !Number.isFinite(Number(decimalHours))) return null;
  const n = Number(decimalHours);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const h = Math.floor(abs);
  let m = Math.round((abs - h) * 60);
  if (m >= 60) m = 59;
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

export function formatRemainingUnitCell(unitKey, value) {
  if (value == null) return null;
  if (unitKey === 'hours') return formatRemainingHoursDisplay(value);
  if (unitKey === 'days') {
    return value < 0 ? `Overdue ${Math.abs(value)}d` : `${value} days`;
  }
  if (unitKey === 'cycles') return `${value} cycles`;
  if (unitKey === 'landings') return `${value} landings`;
  return String(value);
}

export const COMPONENT_UNIT_COLUMN_DEFS = [
  { id: 'remainingDays', label: 'Days', enabledKey: 'days', unitKey: 'days' },
  { id: 'remainingHours', label: 'Hours', enabledKey: 'hours', unitKey: 'hours' },
  { id: 'remainingCycles', label: 'Cycles', enabledKey: 'cycles', unitKey: 'cycles' },
  { id: 'remainingLandings', label: 'Landings', enabledKey: 'landings', unitKey: 'landings' }
];
